import React, { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  LogOut,
  Store,
  Clock,
  MessageSquare,
  Package,
  BarChart3,
  Users,
  UserCog,
  Wand2,
  Layers,
  AlertTriangle,
  Globe,
  Search,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { BRAND_LOGO_DATA_URL } from "../constants/branding";
import StoreAdmin from "./StoreAdmin";
import Settings from "./Settings";
import CatalogAdmin from "./CatalogAdmin";
import Analytics from "./Analytics";
import DetailedLLMAnalytics from "./DetailedLLMAnalytics";
import RolesAdmin from "./RolesAdmin";
import SyncCenter from "./SyncCenter";
import UserManagement from "./UserManagement";
import SessionListPage from "./SessionListPage";
import SessionDetailPage from "./SessionDetailPage";
import LLMPrompts from "./LLMPrompts";
import ButtonTemplatesAdmin from "./ButtonTemplatesAdmin";
import ErrorMonitoring from "./ErrorMonitoring";
import WebsiteCrawlManager from "./WebsiteCrawlManager";
import WebsiteContentSearch from "./WebsiteContentSearch";

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { section, "*": wildcard } = useParams();
  const sessionId = section === "sessions" ? wildcard : undefined;

  // Role-based access control
  const userRole = user?.role || "admin";
  const isSuperAdmin = userRole === "super-admin" || userRole === "super_admin";

  // Set default tab - sessions is now the default for all users
  const defaultTab = "sessions";

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  // Define all tabs with role requirements
  const allTabs = [
    {
      id: "sessions",
      label: "Chats",
      icon: MessageSquare,
      requiresSuperAdmin: false,
    },
    {
      id: "analytics",
      label: "Analytics",
      icon: BarChart3,
      requiresSuperAdmin: false,
    },
    {
      id: "detailed-llm-analytics",
      label: "LLM Analytics",
      icon: Wand2,
      requiresSuperAdmin: true,
    },
    {
      id: "errors",
      label: "Error Logs",
      icon: AlertTriangle,
      requiresSuperAdmin: true,
    },
    {
      id: "catalog",
      label: "Catalog",
      icon: Package,
      requiresSuperAdmin: false,
    },
    {
      id: "stores",
      label: "Stores",
      icon: Store,
      requiresSuperAdmin: false,
    },
    {
      id: "website-crawl",
      label: "Website Crawl",
      icon: Globe,
      requiresSuperAdmin: false,
    },
    {
      id: "website-search",
      label: "Website Search",
      icon: Search,
      requiresSuperAdmin: true,
    },
    {
      id: "sync-center",
      label: "Sync Center",
      icon: Clock,
      requiresSuperAdmin: true,
    },
    {
      id: "settings",
      label: "Settings",
      icon: MessageSquare,
      requiresSuperAdmin: false,
    },
    {
      id: "user-management",
      label: "User Management",
      icon: UserCog,
      requiresSuperAdmin: true,
      showInSidebar: false,
    },
    {
      id: "roles",
      label: "Roles",
      icon: Users,
      requiresSuperAdmin: true,
      showInSidebar: false,
    },
    {
      id: "llm-prompts",
      label: "LLM Prompts",
      icon: Wand2,
      requiresSuperAdmin: true,
      showInSidebar: false,
    },
    {
      id: "button-templates",
      label: "Button Templates",
      icon: Layers,
      requiresSuperAdmin: false,
      showInSidebar: false,
    },
  ];

  const masterDataLinks = [
    { id: "user-management", label: "User Management", icon: UserCog, requiresSuperAdmin: true },
    { id: "roles", label: "Roles", icon: Users, requiresSuperAdmin: true },
    { id: "llm-prompts", label: "LLM Prompts", icon: Wand2, requiresSuperAdmin: true },
    { id: "button-templates", label: "Button Templates", icon: Layers, requiresSuperAdmin: false },
  ];

  // Filter master data links based on user role
  const filteredMasterDataLinks = masterDataLinks.filter(
    (link) => isSuperAdmin || !link.requiresSuperAdmin
  );

  // Filter tabs based on user role
  const tabs = (isSuperAdmin ? allTabs : allTabs.filter((tab) => !tab.requiresSuperAdmin)).filter(
    (tab) => tab.showInSidebar !== false,
  );

  const isKnownTab = section ? allTabs.some((tab) => tab.id === section) : false;
  const activeTab = isKnownTab ? section : defaultTab;

  useEffect(() => {
    if (!section || !isKnownTab) {
      navigate(`/admin/${activeTab}`, { replace: true });
    }
  }, [section, isKnownTab, activeTab, navigate]);

  const renderContent = () => {
    // Check if user has access to this tab
    const currentTab = allTabs.find((tab) => tab.id === activeTab);
    if (currentTab?.requiresSuperAdmin && !isSuperAdmin) {
      return (
        <div className="max-w-4xl mx-auto text-center py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-800 mb-4">Access Denied</h2>
            <p className="text-red-600">You do not have permission to access this section.</p>
            <p className="text-sm text-red-500 mt-2">Contact your administrator for access.</p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case "analytics":
        return <Analytics />;
      case "detailed-llm-analytics":
        return <DetailedLLMAnalytics />;
      case "errors":
        return <ErrorMonitoring />;
      case "catalog":
        return <CatalogAdmin />;
      case "stores":
        return <StoreAdmin />;
      case "website-crawl":
        return <WebsiteCrawlManager />;
      case "website-search":
        return <WebsiteContentSearch />;
      case "sync-center":
        return <SyncCenter />;
      case "sessions":
        return sessionId ? <SessionDetailPage /> : <SessionListPage />;
      case "settings":
      case "nlp-settings":
        return <Settings />;
      case "roles":
        return <RolesAdmin />;
      case "user-management":
        return <UserManagement />;
      case "llm-prompts":
        return <LLMPrompts />;
      case "button-templates":
        return <ButtonTemplatesAdmin />;
      default:
        return <StoreAdmin />;
    }
  };

  // Get current tab details
  const currentTabDetails = allTabs.find((tab) => tab.id === activeTab);

  return (
    <div className="admin-dashboard flex h-screen bg-gray-50 overflow-hidden">
      {/* Dark Sidebar Navigation */}
      <div className="w-56 bg-[#1e293b] text-white flex flex-col">
        {/* Logo Section */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden border border-white/10 shadow-lg">
              <img
                src={BRAND_LOGO_DATA_URL}
                alt="BeanBot logo"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">BeanBot</h1>
            </div>
          </div>
        </div>

        {/* Navigation Section */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          <div className="space-y-1.5">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-3">
              Navigation
            </div>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <Link
                  key={tab.id}
                  to={`/admin/${tab.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm font-medium no-underline hover:no-underline focus-visible:no-underline ${
                    isActive
                      ? "bg-blue-600 text-white shadow-lg"
                      : "text-gray-300 hover:bg-gray-700 hover:text-white"
                  }`}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Master Data Section */}
          {filteredMasterDataLinks.length > 0 && (
            <div className="mt-6">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-3">
                Master Data
              </div>
              <div className="space-y-2">
                {filteredMasterDataLinks.map((link) => {
                  const Icon = link.icon;
                  const isActive = activeTab === link.id;

                  return (
                    <Link
                      key={link.id}
                      to={`/admin/${link.id}`}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm font-medium no-underline hover:no-underline focus-visible:no-underline ${
                        isActive
                          ? "bg-blue-600 text-white shadow-lg"
                          : "text-gray-300 hover:bg-gray-700 hover:text-white"
                      }`}
                      aria-current={isActive ? "page" : undefined}
                    >
                      <Icon size={18} />
                      <span>{link.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </nav>

        {/* User Profile Section */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold">{user?.email?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.email}</p>
              <p className="text-xs text-gray-400">
                {userRole === "super-admin" ? "Super Admin" : "Admin"}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm font-medium"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header Bar */}
        <header className="bg-white border-b border-gray-200 px-5 py-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                {currentTabDetails && (
                  <>
                    <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center">
                      <currentTabDetails.icon size={18} className="text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{currentTabDetails.label}</h2>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-5">
          <div className="max-w-7xl mx-auto">{renderContent()}</div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
